import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import { Box, CircularProgress, Stack } from "@mui/material";
import {
  CaseStatus,
  type Visit,
  VISIT_TYPE_LABELS,
  VisitStatus,
  VisitType,
} from "@src/appV2/ExperimentalHomeHealth/Cases/types";
import { addWeeks } from "date-fns";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { useAgencyCases } from "@src/appV2/ExperimentalHomeHealth/Cases/api/useAgencyCases";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";

import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Icon } from "../../components/Icon";
import { Text } from "../../components/Text";
import { DEFAULT_TIME_ZONE, FACILITY_TIMEZONE_PROJECTION } from "../../MyProfessionals/constants";
import { useInviteWorkerToCaseVisit } from "../api/useInviteWorkerToCaseVisit";

interface InviteToCaseDialogProps {
  workerId: string;
  workerName: string;
  workplaceId: string;
  modalState: UseModalState;
}

function eligibleVisitDate(
  confirmedVisitDate: string | undefined,
  deadline: string | undefined,
  timeZone: string
): string | undefined {
  const date = confirmedVisitDate ?? deadline;
  return date ? formatShortMonthDayYear(new Date(date), { timeZone }) : undefined;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Whether an OPEN visit is still a valid invite target as of `todayStart`:
 *  - non-REGULAR:        valid if its date is today-or-later
 *  - recurring weekly:   valid until start + durationInWeeks
 *  - recurring biweekly: valid until endDate
 * When the end can't be determined, keep the visit (don't hide a maybe-valid one).
 */
function isVisitStillValid(visit: Visit, todayStart: number): boolean {
  const { type, confirmedVisitDate, deadline, weekInterval, durationInWeeks, endDate } =
    visit.attributes;
  const start = confirmedVisitDate ?? deadline;

  if (type !== VisitType.REGULAR) {
    if (!start) {
      return true;
    }
    return startOfDay(new Date(start)) >= todayStart;
  }

  // Biweekly recurrence ends at endDate.
  if (weekInterval === 2) {
    if (!endDate) {
      return true;
    }
    return startOfDay(new Date(endDate)) >= todayStart;
  }

  // Weekly recurrence ends durationInWeeks after the start.
  if (!start || !durationInWeeks) {
    return true;
  }
  return startOfDay(addWeeks(new Date(start), durationInWeeks)) >= todayStart;
}

/**
 * "Invite to case" dialog (from the worker chat panel). Invites are per-visit,
 * so it lists the workplace's eligible OPEN visits as selectable cards (styled
 * like LTC "Invite to Shift") and invites the worker to the chosen one.
 */
export function InviteToCaseDialog(props: InviteToCaseDialogProps) {
  const { workerId, workerName, workplaceId, modalState } = props;
  const { tu } = useTu("myProfessionals");
  const { showSuccessToast, showErrorToast } = useToast();
  const [selectedVisitId, setSelectedVisitId] = useState<string>("");

  // Format visit dates in the workplace timezone (matches VisitRow). Gated on
  // the dialog being open — callers render this element persistently.
  const { data: facilityProfile } = useGetFacilityProfile(
    workplaceId,
    FACILITY_TIMEZONE_PROJECTION,
    {
      enabled: modalState.modalIsOpen,
    }
  );
  const timeZone = facilityProfile?.tmz ?? DEFAULT_TIME_ZONE;

  const {
    data: cases,
    isLoading,
    isError,
  } = useAgencyCases({ filter: { status: CaseStatus.OPEN } }, { enabled: modalState.modalIsOpen });

  const { mutateAsync: inviteWorker, isLoading: isInviting } = useInviteWorkerToCaseVisit({
    workplaceId,
  });

  // Eligible targets: OPEN visits in active cases the worker isn't already
  // invited to and that are still valid (see isVisitStillValid).
  const todayStart = startOfDay(new Date());
  const eligibleVisits = (cases ?? []).flatMap((caseItem) =>
    caseItem.attributes.visits
      .filter(
        (visit) =>
          visit.attributes.status === VisitStatus.OPEN &&
          !visit.attributes.invitedWorkersIds.includes(workerId) &&
          isVisitStillValid(visit, todayStart)
      )
      .map((visit) => ({
        visit,
        patientIdentifier: caseItem.attributes.patient.attributes.externalPatientIdentifier,
      }))
  );

  const hasEligibleVisits = eligibleVisits.length > 0;

  async function handleInvite() {
    if (!selectedVisitId) {
      return;
    }

    try {
      await inviteWorker({ visitId: selectedVisitId, workerId });
      showSuccessToast(tu("Invited {{workerName}} to the visit.", { values: { workerName } }));
      modalState.closeModal();
    } catch {
      showErrorToast(tu("Could not send the invite. Please try again."));
    }
  }

  return (
    <Dialog
      modalState={modalState}
      title={tu("Invite to case")}
      // Clamp the dialog so long visit lists scroll inside instead of
      // stretching the modal to near-viewport height. The calc() term keeps
      // it within scoped modal containers (e.g. side panels).
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "min(70vh, calc(100% - 64px))",
        },
      }}
      footerContent={
        // Redesign Dialog renders footerContent flush, so inset the button.
        <Box sx={{ padding: 5 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedVisitId || isInviting}
            onClick={() => {
              void handleInvite();
            }}
          >
            {isInviting ? tu("Sending…") : tu("Send Invite")}
          </Button>
        </Box>
      }
    >
      {isLoading ? (
        <Stack sx={{ alignItems: "center", paddingY: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : null}

      {isError ? (
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
          {tu("Could not load cases.")}
        </Text>
      ) : null}

      {!isLoading && !isError && !hasEligibleVisits ? (
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {tu("No open visits available to invite {{workerName}} to.", { values: { workerName } })}
        </Text>
      ) : null}

      {hasEligibleVisits ? (
        <Stack spacing={4}>
          <Box>
            <Text
              semibold
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, marginBottom: 2 })}
            >
              {tu("Visit")}
            </Text>
            <Stack spacing={2}>
              {eligibleVisits.map(({ visit, patientIdentifier }) => {
                const isSelected = visit.id === selectedVisitId;
                const dateLabel = eligibleVisitDate(
                  visit.attributes.confirmedVisitDate,
                  visit.attributes.deadline,
                  timeZone
                );
                const subtitle = [
                  VISIT_TYPE_LABELS[visit.attributes.type],
                  visit.attributes.workerReq,
                  dateLabel,
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <Box
                    key={visit.id}
                    aria-checked={isSelected}
                    aria-label={`${patientIdentifier} ${subtitle}`}
                    role="radio"
                    tabIndex={0}
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      padding: 3,
                      borderRadius: theme.borderRadius?.medium,
                      border: `1px solid ${
                        isSelected ? theme.palette.base?.primary : theme.palette.border?.base
                      }`,
                      backgroundColor: isSelected
                        ? `${theme.palette.base?.primary}08`
                        : "transparent",
                      cursor: "pointer",
                      "&:hover": { borderColor: theme.palette.base?.primary },
                    })}
                    onClick={() => {
                      setSelectedVisitId(visit.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedVisitId(visit.id);
                      }
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: `${theme.palette.base?.primary}14`,
                      })}
                    >
                      <Icon type="house-building" size="small" />
                    </Box>
                    <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                      <Text
                        semibold
                        variant="body2"
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                      >
                        {patientIdentifier}
                      </Text>
                      <Text
                        variant="caption"
                        sx={(theme) => ({ color: theme.palette.text.secondary })}
                      >
                        {subtitle}
                      </Text>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      ) : null}
    </Dialog>
  );
}
